import { useMemo, useState } from "react";
import CommonTable from "../../components/commonTable";
import CustomPagination from "../../components/pagination";
import AddButton from "../../components/addButton";
import { useGetAllBankDepositsQuery } from "../../services/bankDeposit";
import { BankDepositRoute } from "../../routes/routepath";
import "./styles.scss";

/* ─── Types ──────────────────────────────────────────────────────────── */

interface DepositRecord {
  key:            string;
  dateTime?:      string;
  bankName?:      string;
  accountNumber?: string;
  branch?:        string;
  amount?:        number;
  remark?:        string;
  receiptImage?:  string;
}

/* ─── Constants ──────────────────────────────────────────────────────── */

const renderAmount = (v?: number) =>
  v === undefined || v === null ? '-' : <span style={{ color: '#52c41a' }}>₹{v.toLocaleString('en-IN')}</span>;

const renderDateTime = (v?: string) => {
  if (!v) return '-';
  const dt = new Date(v);
  if (isNaN(dt.getTime())) return v;
  return (
    <div>
      <div>{dt.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}</div>
      <div style={{ fontSize: 12, opacity: 0.65 }}>{dt.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}</div>
    </div>
  );
};

/* ─── Component ──────────────────────────────────────────────────────── */

const DepositHistory = () => {
  const [page,  setPage]  = useState(1);
  const [limit, setLimit] = useState(20);

  const { data: depositsResp, isFetching } = useGetAllBankDepositsQuery({ page, limit });

  const apiData = depositsResp as any;

  const records = useMemo<DepositRecord[]>(() => {
    const rows: any[] = apiData?.data || [];
    return rows.map((d: any) => ({
      key:           d._id,
      dateTime:      d.createdAt || d.dateTime,
      bankName:      d.bankId?.bankName || d.bank?.bankName,
      accountNumber: d.bankId?.accountNumber || d.bank?.accountNumber,
      branch:        d.branchId?.name || d.branch?.name,
      amount:        d.amount,
      remark:        d.remark,
      receiptImage:  d.receiptImage,
    }));
  }, [apiData]);

  const columns = [
    { title: 'Date',           dataIndex: 'dateTime',      key: 'dateTime',      render: renderDateTime },
    { title: 'Bank',           dataIndex: 'bankName',      key: 'bankName',      render: (v?: string) => v || '-' },
    { title: 'Account Number', dataIndex: 'accountNumber', key: 'accountNumber', render: (v?: string) => v || '-' },
    { title: 'Branch',         dataIndex: 'branch',        key: 'branch',        render: (v?: string) => v || '-' },
    { title: 'Amount',         dataIndex: 'amount',        key: 'amount',        render: renderAmount },
    { title: 'Remark',         dataIndex: 'remark',        key: 'remark',        render: (v?: string) => v || '-' },
    { title: 'Receipt',        dataIndex: 'receiptImage',  key: 'receiptImage',  align: 'center' as const,
      render: (v?: string) => v ? <a href={v} target="_blank" rel="noreferrer">View</a> : '-' },
  ];

  return (
    <div className="deposit-history-page">
      <div className="header-section">
        <h2 style={{ margin: 0 }}>Deposit History</h2>
        <div className="right-col">
          <AddButton to={BankDepositRoute}>Bank Deposit</AddButton>
        </div>
      </div>

      <div className="deposit-table-wrapper">
        <CommonTable
          columns={columns}
          dataSource={records}
          loading={isFetching}
          pagination={false}
          rowKey={(record: DepositRecord) => record.key}
        />
      </div>

      <CustomPagination
        current={page}
        pageSize={limit}
        total={apiData?.total || 0}
        onPageChange={(newPage: number) => setPage(newPage)}
        onPageSizeChange={(newLimit: number) => { setLimit(newLimit); setPage(1); }}
      />
    </div>
  );
};

export default DepositHistory;
